import { Dimensions, MediaImage } from 'editor/src/store/design/types';

import getPointPositionRotatedOnPoint from 'editor/src/util/getPointPositionRotatedOnPoint';
import limitPrecision from 'editor/src/util/limitPrecision';

import getDigitizedSizeMm from './getDigitizedSizeMm';

// places a digitized image inside the element frame keeping the digitized aspect ratio and, when
// known, the digitized physical size, so the existing digitization can be reused without a new fee
function getDigitizedImagePosition(
  element: MediaImage,
  dimensions: Dimensions,
  digitizedSizeMm: Dimensions | undefined,
) {
  const { width: imageWidth, height: imageHeight } = dimensions;
  const hasImageDimensions = imageWidth > 0 && imageHeight > 0;
  const digitizedSize = getDigitizedSizeMm(digitizedSizeMm, dimensions);

  let width: number;
  let height: number;
  if (digitizedSize) {
    // place at the resolved digitized size so the existing digitization is reused
    width = limitPrecision(digitizedSize.width);
    height = limitPrecision(digitizedSize.height);
  } else if (hasImageDimensions) {
    // no digitized size known: fit inside the frame keeping the aspect ratio
    const fitScale = Math.min(element.width / imageWidth, element.height / imageHeight);
    width = limitPrecision(imageWidth * fitScale);
    height = limitPrecision((width * imageHeight) / imageWidth);
  } else {
    // without valid image dimensions we cannot derive the aspect ratio, so keep the frame's own size
    width = element.width;
    height = element.height;
  }

  // keep the resized element visually centered on the frame's center; elements rotate around their
  // top-left (x, y), so we rotate both the frame center and the new half-extent to place x/y right
  const rotation = element.r || 0;
  const [centerX, centerY] = getPointPositionRotatedOnPoint(
    element.x + element.width / 2,
    element.y + element.height / 2,
    element.x,
    element.y,
    rotation,
  );
  const [halfX, halfY] = getPointPositionRotatedOnPoint(width / 2, height / 2, 0, 0, rotation);

  return {
    x: limitPrecision(centerX - halfX),
    y: limitPrecision(centerY - halfY),
    width,
    height,
    px: 0,
    py: 0,
    pw: width,
    ph: height,
  };
}

export default getDigitizedImagePosition;
